import { CheckboxQuestions, SubQuestion } from "../../types/QuestionTypes";

const removeMatchingKeys = (obj: Record<string, any>): Record<string, any> => {
  return Object.keys(obj).reduce((acc, key) => {
    // Remove the 'true' and 'false' keys from the object
    if (key === "true" || key === "false") {
      return acc;
    }

    // Keep existing logic for removing keys based on specific regex patterns
    if (!key.match(/^fieldName_[a-f0-9\-]+_[a-f0-9\-]+/) && !key.match(/^fieldType_[a-f0-9\-]+_[a-f0-9\-]+/) && !key.match(/^isRequired_[a-f0-9\-]+_[a-f0-9\-]+/)) {
      acc[key] = obj[key];
    }

    return acc;
  }, {} as Record<string, any>);
};

const formattedOptions = (data: { id: string | number; name: string; subQuestion: SubQuestion[] }[]) => {
  if (data.length === 0) return [];

  return data?.map((option) => ({
    ...(typeof option.id === "string" && { id: option.id }),
    name: option.name,
    subQuestion:
      option.subQuestion.length > 0
        ? option.subQuestion
            ?.filter((question) => question.fieldName)
            ?.map((sub) => ({
              ...(typeof sub.id === "string" && { id: sub.id }),
              fieldName: sub.fieldName,
              fieldType: sub.fieldType,
              isRequired: sub.isRequired ?? false,
            }))
        : [],
  }));
};

const transformData = (data: any) => {
  if (data.length === 0) return [];

  return data?.map((item: any) => ({
    id: item.id,
    name: item.name,
    subQuestion: item.subQuestion.map((sub: any) => ({
      id: sub.id,
      name: sub.fieldName,
      type: sub.fieldType,
      required: sub.isRequired,
    })),
  }));
};

const transformObject = (data: any) => {
  if (!data) return data;

  // Iterate through each key ('true' and 'false') in the input object
  return Object?.keys(data)?.map((key) => {
    return {
      name: key,
      subQuestion: data[key]
        ?.filter((question: any) => question.fieldName)
        ?.map((item: any) => {
          return {
            fieldName: item.fieldName,
            fieldType: item.fieldType,
            isRequired: item.isRequired,
          };
        }),
    };
  });
};

const transformCheckboxData = (data: CheckboxQuestions[]) => {
  if (data.length === 0) return [];

  return data?.map((option) => ({
    ...(typeof option.id === "string" && { id: option.id }),
    name: option.name,
    subQuestion:
      option.subQuestion.length > 0
        ? option.subQuestion
            ?.filter((question) => question.fieldName)
            ?.map((sub) => ({
              ...(typeof sub.id === "string" && { id: sub.id }),
              fieldName: sub.fieldName,
              fieldType: sub.fieldType,
              isRequired: sub.isRequired ?? false,
            }))
        : [],
  }));
};

const sortByTrueFirst = (data: CheckboxQuestions[]): CheckboxQuestions[] => {
  if (data.length === 0) return [];

  return data?.sort((a, b) => {
    // If `name` is "true", it should come before "false"
    if (a.name === "true" && b.name !== "true") {
      return -1; // `a` comes before `b`
    }
    if (a.name !== "true" && b.name === "true") {
      return 1; // `b` comes before `a`
    }
    return 0; // If both are "true" or both are "false", no change in order
  });
};

export { removeMatchingKeys, formattedOptions, transformData, transformObject, transformCheckboxData, sortByTrueFirst };
